import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import IndiaMapSvg from "./IndiaMapSvg";
import { svgStateIdToBackendStateIdMap } from "../../utils/svgStateIdToStateIdMap";
import { CountryStats, StateOverviewStat } from "../CountryStats";

export type IndiaMapProps = Partial<{
  selectedStateId: string;
  onStateSelect: (stateId: string) => any;
  onDeselect: (...args: any[]) => any;
  imgStyle: any;
  imgClassName: string;
  stateOverviewStats: StateOverviewStat[];
  countryStats: CountryStats[];
}>;

const getBackendIdFromSvgStateElementId = (stateElementId: string): string =>
  svgStateIdToBackendStateIdMap[stateElementId] || "-1";

const updateStateElementColor = (
  stateElement: SVGPathElement,
  onboardingScore: number,
  highestScore: number,
  isSelected: boolean,
) => {
  const darkColorRgb = [2, 20, 45];
  const lightnessCoeffecient = (highestScore - onboardingScore) / highestScore;
  const rgb = [
    Math.ceil(lightnessCoeffecient * (210 - darkColorRgb[0])) + darkColorRgb[0],
    Math.ceil(lightnessCoeffecient * (210 - darkColorRgb[1])) + darkColorRgb[1],
    Math.ceil(lightnessCoeffecient * (210 - darkColorRgb[2])) + darkColorRgb[2],
  ];
  stateElement.style.fill = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;

  stateElement.style.opacity = isSelected ? "1" : "0.5";
};
const top10Colors = [
  "#1E3A8A",
  "#DC2626",
  "#1D4ED8",
  "#3B82F6",
  "#60A5FA",
  "#9333EA",
  "#7C3AED",
  "#F59E0B",
  "#2563EB",
  "#059669",
];

export default function IndiaMap(props: IndiaMapProps) {
  const {
    selectedStateId,
    onStateSelect = () => {},
    imgStyle,
    imgClassName,
    stateOverviewStats,
    countryStats,
  } = props;
  const [top10States, setTop10States] = useState<
    { stateName: string; total: number }[]
  >([]);

  const svgRef = useRef<SVGSVGElement>(null);

  const svgOnClick = useCallback<MouseEventHandler<SVGElement>>(
    (e) => {
      const target = e.target as SVGPathElement;
      if (target.id.startsWith("IN")) {
        onStateSelect(getBackendIdFromSvgStateElementId(target.id));
      }
    },
    [onStateSelect],
  );
  const updateStateColors = (
    stateElements: SVGPathElement[],
    stateOverviewStats: StateOverviewStat[] | undefined,
    selectedStateId: string | undefined,
    svgElement: SVGSVGElement | null,
    countryStats?: CountryStats[],
  ) => {
    if (!svgElement) return;

    const isDefaultView =
      selectedStateId === undefined ||
      selectedStateId === null ||
      selectedStateId === "";

    const oldTexts = svgElement.querySelectorAll(".state-label");
    oldTexts.forEach((t) => t.remove());

    if (isDefaultView && countryStats && countryStats.length > 0) {
      const stateTotals: Record<string, number> = {};

      countryStats.forEach((cs) => {
        if (!cs.stateName) return;
        stateTotals[cs.stateName] =
          (stateTotals[cs.stateName] || 0) + cs.onboardingCount;
      });

      const top10 = Object.entries(stateTotals)
        .map(([stateName, total]) => ({ stateName, total }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 10);
      setTop10States(top10);
      stateElements.forEach((stateElement) => {
        const svgId = stateElement.id;
        const matchingState = top10.find((s) => {
          const mappedStateName = svgStateIdToBackendStateIdMap[svgId];

          return mappedStateName === s.stateName;
        });
        if (matchingState) {
          const index = top10.findIndex(
            (s) => s.stateName === matchingState.stateName,
          );

          stateElement.style.fill = top10Colors[index];
          stateElement.style.opacity = "1";
        } else {
          stateElement.style.fill = "#E5E7EB";
          stateElement.style.opacity = "0.5";
        }
      });

      return;
    }

    stateElements.forEach((stateElement) => {
      const backendId = getBackendIdFromSvgStateElementId(stateElement.id);

      if (backendId === selectedStateId) {
        stateElement.style.fill = "#2563EB";
        stateElement.style.opacity = "1";
      } else {
        stateElement.style.fill = "#D1D5DB";
        stateElement.style.opacity = "0.5";
      }
    });
    setTop10States([]);
  };
  useEffect(() => {
    if (!svgRef.current) return;
    if (!stateOverviewStats || stateOverviewStats.length === 0) return;

    const statePathElements = Array.from(
      svgRef.current.getElementsByTagName("path"),
    );

    updateStateColors(
      statePathElements,
      stateOverviewStats,
      selectedStateId,
      svgRef.current,
      countryStats,
    );
  }, [selectedStateId, stateOverviewStats, countryStats]);

  return (
    <div className="w-full relative">
      <IndiaMapSvg
        ref={svgRef}
        style={imgStyle}
        className={`w-full ${imgClassName}`}
        onClick={svgOnClick}
      />

      {top10States.length > 0 && (
        <div className="absolute top-60 right-2 bg-white/90 backdrop-blur-sm rounded-md shadow-md p-2 space-y-1 max-w-[220px]">
          {top10States.map((state, index) => (
            <div
              key={state.stateName}
              className="flex items-center justify-between text-xs px-2 py-1 rounded-sm"
              style={{
                backgroundColor: top10Colors[index],
                color: "white",
              }}
            >
              <span className="truncate">{state.stateName}</span>
              <span className="font-semibold">{state.total}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
